"use client"
import * as React from "react"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, LabelList } from "recharts"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card"
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart"
import { DatePickerWithRange } from "./ShadDateRangePicker"
import { DatePickerDemo } from "./ShadDatePicker"
import { Button } from "../ui/Button"

function compactCount(n) {
  const v = Number(n) || 0;
  if (v >= 10000) return `${Math.round(v / 1000)}k`;
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return v.toLocaleString("he-IL");
}

const CustomTotalLabel = ({ x, y, width, value }) => {
  if (x == null || y == null || value == null || Number(value) === 0) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 7}
      textAnchor="middle"
      fill="#374151"
      fontSize={11}
      fontWeight="600"
    >
      {compactCount(value)}
    </text>
  );
};


export function ShadStackedBarChart({ chartConfig = {}, chartData = [], deviceTypes = Object.keys(chartConfig), calenderStartDate, setCalenderStartDate, calenderEndDate, setCalenderEndDate, handleDateSelect, handleDateSubmit, calenderComponentDisabled, cardTitle, cardDescription, buttonText, showTotal = false, presetButtons, filterElement }) {

  return (
    <Card className="h-full flex flex-col justify-between">
      <CardHeader className="flex items-center gap-4 space-y-0 justify-between border-b py-3 lg:flex-row min-h-[73px]" >
        <div className="flex flex-col gap-3 max-w-[800px] lg:flex-row lg:items-center flex-wrap" >
          <DatePickerDemo calenderComponentDisabled={calenderComponentDisabled} label={"start"} date={calenderStartDate} setDate={handleDateSelect} />
          <DatePickerDemo calenderComponentDisabled={calenderComponentDisabled} label={"end"} date={calenderEndDate} setDate={handleDateSelect} />
          {buttonText && <Button disabled={calenderComponentDisabled} onClick={handleDateSubmit} >{buttonText}</Button>}
          {presetButtons && <div className="flex gap-1.5 flex-wrap">{presetButtons}</div>}
          {filterElement && <div className="ml-2">{filterElement}</div>}
        </div>
        <div className="grid gap-1 w-full text-right">
          <CardTitle>{cardTitle}</CardTitle>
          <CardDescription>{cardDescription}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 flex flex-col justify-center">
        <ChartContainer className="aspect-auto h-[303px] w-full" config={chartConfig}>
          <BarChart accessibilityLayer data={chartData} margin={{ top: 28, right: 8, left: 4, bottom: 0 }} >
            <CartesianGrid vertical={false} />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={6}
              width={40}
              tick={{ fontSize: 10, fill: "#6b7280" }}
              tickFormatter={(value) => compactCount(value)}
              allowDecimals={false}
            />
            <XAxis
              dataKey="month"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              tickFormatter={(value) => value}
            />
            <ChartTooltip content={<ChartTooltipContent

              labelFormatter={(value) => {
                return value;
              }}

              indicator="dot"
            />} />
            <ChartLegend content={<ChartLegendContent />} />
            {deviceTypes.map((device, i) => {
              const isLast = i === deviceTypes.length - 1;
              return (
                <Bar
                  key={device}
                  dataKey={device}
                  stackId="a"
                  fill={`var(--color-${device})`}
                  radius={i === 0 ? [0, 0, 4, 4] : isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              );
            })}
            {showTotal && (
              <Bar
                dataKey="_stackLabel"
                stackId="a"
                fill="transparent"
                legendType="none"
                isAnimationActive={false}
              >
                <LabelList dataKey="total" position="top" content={<CustomTotalLabel />} />
              </Bar>
            )}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
